import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Pie, Bar } from "react-chartjs-2";
import {
  getUsersInfos,
  getOrgsInfos,
  getNumberofFolders,
  getLoggedUsers,
  getRequestLogs,
  getFinances,
  getRequestOnline,
  getRequestFeedback,
} from "../api/admin/dashboard";
import { useAppContext } from "../config/AppContext";
import { getOrRefreshFirebaseToken } from "../config/getFirebaseToken";
import LineChart1 from "../charts/LineChart1";
import { useNavigate } from "react-router-dom";
import PieChart1 from "../charts/PieChart1";
import { set } from "date-fns";
import { getDocDataById } from "../models/DataFunctions";
import { valueNanCheck } from "../pages/home/MasterStats";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const AdminDashboard = () => {
  const [userData, setUserData] = useState(null);
  const [orgData, setOrgData] = useState(null);
  const [loggedUsers, setLoggedUsers] = useState(null);
  const [folderCount, setFolderCount] = useState(0);
  const [requestLogs, setRequestLogs] = useState([]);
  const [activeTab, setActiveTab] = useState("Monitoring");
  const { appState } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOrgs, setFilteredOrgs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [finances, setFinances] = useState(null);
  const [requestOnline, setRequestOnline] = useState(null);
  const [users_connected, setUsersConnected] = useState([]);
  const [requestFeedback, setRequestFeedback] = useState(null);
  const itemsPerPage = 12;

  useEffect(() => {
    if (orgData?.org_count) {
      const filtered = orgData.org_count.filter((org) =>
        org.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOrgs(filtered);
      setCurrentPage(1); // Reset to the first page on search
    }
  }, [searchTerm, orgData]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await getOrRefreshFirebaseToken();

        const [
          folderRes,
          userRes,
          orgRes,

          financesRes,
          onlineRes,
          feedbackRes,
        ] = await Promise.all([
          getNumberofFolders(prefix, token),
          getUsersInfos(prefix, token),
          getOrgsInfos(prefix, token),

          getFinances(prefix, token),
          getRequestOnline(prefix, token),
          getRequestFeedback(prefix, token),
        ]);
        const admin_stats = await getDocDataById("admin", "stats");
        let daily_users_connected_count =
          admin_stats.daily_users_connected_count
            .sort((a, b) => {
              return a.date.seconds - b.date.seconds;
            })
            .slice(-30); // invert the order of the array

        let max_daily_users_connected_count = Math.max(
          ...daily_users_connected_count.map((item) => item.value)
        );
        daily_users_connected_count = daily_users_connected_count.map(
          (item) => {
            return {
              ...item,
              height: Math.round(
                (item.value / max_daily_users_connected_count) * 80
              ),
            };
          }
        );
        setUsersConnected(daily_users_connected_count);

        setFolderCount(folderRes);

        setFinances(financesRes);
        setRequestOnline(onlineRes);
        setRequestFeedback(feedbackRes);
        setUserData(userRes);
        setOrgData(orgRes);

        const [loggedUsersRes, logsRes] = await Promise.all([
          getLoggedUsers(prefix, token),
          getRequestLogs(prefix, token),
        ]);
        setLoggedUsers(loggedUsersRes);
        setRequestLogs(logsRes);
      } catch (err) {
        console.error("Error fetching admin data:", err);
      }
    };

    fetchData();
  }, [prefix]);

  const paginatedOrgs = filteredOrgs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(filteredOrgs.length / itemsPerPage);
  // Process data for Response Time graph
  const responseTimes = requestLogs.map((log) => log.response_time || 0);
  const timestamps = requestLogs.map((log) => log.timestamp || ""); // HH:MM:SS format

  const responseTimeData = {
    labels: timestamps,
    datasets: [
      {
        label: "Response Time (ms)",
        data: responseTimes,
        borderColor: "#4F46E5",
        backgroundColor: "#4F46E5",
        tension: 0.1,
      },
    ],
  };

  // Calculate TPM (Transactions Per Minute)
  const tpmData = requestLogs.reduce((acc, log) => {
    const timestamp = log.timestamp || "00:00:00";
    const minute = timestamp.slice(0, 5); // Extract HH:MM
    acc[minute] = (acc[minute] || 0) + 1;
    return acc;
  }, {});

  const tpmChartData = {
    labels: Object.keys(tpmData),
    datasets: [
      {
        label: "Transactions Per Minute (TPM)",
        data: Object.values(tpmData),
        backgroundColor: "#4F46E5",
      },
    ],
  };

  const FeedbackChartData = userData
    ? {
        labels: ["Positive", "Negative"],
        datasets: [
          {
            data: [requestFeedback.positive, requestFeedback.negative],
            backgroundColor: ["#4BC0C0", "#FF6384"],
          },
        ],
      }
    : { labels: [], datasets: [] };

  const FeedbackRepartitionChartData = orgData
    ? {
        labels: Object.keys(requestFeedback.ratings || {}),
        datasets: [
          {
            label: "Ratings",
            data: requestFeedback.ratings,
            backgroundColor: [
              "#FF6384",
              "#FF6384",
              "#FF6384",
              "#FF6384",
              "#FF6384",
              "#FF6384",
              "#FF6384",
              "#FFCE56",
              "#FFCE56",
              "#4BC0C0",
              "#4BC0C0",
            ],
          },
        ],
      }
    : { labels: [], datasets: [] };

  const userTypesChartData = userData
    ? {
        labels: Object.keys(userData).filter((key) => key !== "total"),
        datasets: [
          {
            label: "User Types",
            data: Object.keys(userData)
              .filter((key) => key !== "total")
              .map((key) => userData[key]),
            backgroundColor: [
              "#FF6384",
              "#36A2EB",
              "#FFCE56",
              "#4BC0C0",
              "#9966FF",
            ],
          },
        ],
      }
    : { labels: [], datasets: [] };

  const orgPlansChartData = orgData
    ? {
        labels: Object.keys(orgData.plan || {}),
        datasets: [
          {
            label: "Plan Types",
            data: Object.values(orgData.plan || {}),
            backgroundColor: [
              "#FF6384",
              "#36A2EB",
              "#FFCE56",
              "#4BC0C0",
              "#9966FF",
            ],
          },
        ],
      }
    : { labels: [], datasets: [] };

  const loggedUsersChartData = loggedUsers
    ? {
        labels: Object.keys(loggedUsers.users_id_per_hour || {}).map(
          (hour) => `${hour}:00`
        ),
        datasets: [
          {
            label: "Users Logged In Per Hour",
            data: Object.values(loggedUsers.users_id_per_hour || {}),
            fill: false,
            borderColor: "#4F46E5",
            backgroundColor: "#4F46E5",
            tension: 0.1,
          },
        ],
      }
    : { labels: [], datasets: [] };

  return (
    <div className="p-6 px-12 w-full overflow-auto">
      <h1 className="text-2xl font-bold mb-8">Admin Dashboard</h1>

      {/* Tabs Navigation */}
      <div className="flex border-b mb-6">
        <button
          className={`py-2 px-4 ${
            activeTab === "Monitoring"
              ? "border-b-2 border-blue-500 text-blue-500"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("Monitoring")}
        >
          Monitoring
        </button>
        <button
          className={`py-2 px-4 ${
            activeTab === "Financials"
              ? "border-b-2 border-green-500 text-green-500"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("Financials")}
        >
          Financials
        </button>
      </div>

      {/* Tabs Content */}
      {activeTab === "Monitoring" && (
        <div>
          {/* Stat Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2 mb-2">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-lg font-semibold text-gray-700">Online</h2>
              <div className="text-3xl font-bold text-gray-900 flex flex-row gap-2  items-center">
                {userData ? requestOnline.total_online : "Loading..."}
                {userData && requestOnline.total_online > 0 && (
                  <div className="w-3 h-3 bg-green_simple rounded-full"></div>
                )}
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-lg font-semibold text-gray-700">
                Total Users
              </h2>
              <p className="text-3xl font-bold text-gray-900">
                {userData ? userData.total : "Loading..."}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-lg font-semibold text-gray-700">
                Total Organizations
              </h2>
              <p className="text-3xl font-bold text-gray-900">
                {orgData ? orgData.total : "Loading..."}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-lg font-semibold text-gray-700">
                Total Folders
              </h2>
              <p className="text-3xl font-bold text-gray-900">
                {folderCount ? folderCount : "Loading..."}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-lg font-semibold text-gray-700">
                Users Today
              </h2>
              <p className="text-3xl font-bold text-gray-900">
                {loggedUsers ? loggedUsers.total_users : "Loading..."}
              </p>
            </div>
          </div>

          {/* Charts */}
          <div className="bg-white p-6 rounded-lg shadow-md mb-2">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">
              Online Users
            </h2>
            <div className="max-h-64 overflow-y-auto">
              {requestOnline?.users_id?.length > 0 ? (
                <ul className="list-disc ">
                  {requestOnline.users_id?.map((user, index) => (
                    <li
                      key={index}
                      className="text-gray-700 flex flex-row gap-4 items-center text-h3"
                    >
                      <div className="max-w-8 h-8 rounded-full bg-primary_l3 flex justify-center relative">
                        <img
                          className="max-w-8 h-8 rounded-full min-w-8 min-h-8 object-cover"
                          src={
                            user.photo ||
                            "https://upload.wikimedia.org/wikipedia/commons/a/ac/Default_pfp.jpg?20200418092106"
                          }
                        ></img>
                      </div>
                      {user.email}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500">No emails available.</p>
              )}
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md mb-2">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">
              User connected
            </h2>
            <div className="flex flex-row ">
              {users_connected.map((item, index) => (
                <div
                  key={index}
                  className="flex flex-col  text-h5 w-c40 items-center"
                >
                  <div
                    className="bg-blue-500 rounded-xl w-1 mt-auto mb-1"
                    style={{
                      height: valueNanCheck(item.height),
                    }}
                  ></div>
                  <div className="my-1 text-blue-500 text-h2">{item.value}</div>
                  <div className="text-h6">
                    {new Date(item.date.seconds * 1000)
                      .toLocaleDateString()
                      .substring(0, 5)}
                  </div>{" "}
                  <div className="text-h7 font-semibold text-shadow_25">
                    {new Date(item.date.seconds * 1000)
                      .toLocaleDateString()
                      .substring(6, 10)}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md mb-2">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">
              Logged Users' Emails
            </h2>
            <div className="max-h-64 overflow-y-auto">
              {loggedUsers?.unique_emails?.length > 0 ? (
                <ul className="list-disc pl-6 text-h3">
                  {loggedUsers.unique_emails?.map((email, index) => (
                    <li key={index} className="text-gray-700">
                      {email}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500">No emails available.</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-2">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-lg font-semibold mb-4">
                User Types Distribution
              </h2>
              <div className="w-full h-80">
                <Pie
                  data={userTypesChartData}
                  options={{ maintainAspectRatio: false }}
                />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-lg font-semibold mb-4">
                Organization Plans Distribution
              </h2>
              <div className="w-full h-80">
                <Bar
                  data={orgPlansChartData}
                  options={{ maintainAspectRatio: false }}
                />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-lg font-semibold mb-4">
                User Activity Today (Hourly)
              </h2>
              <div className="w-full h-80">
                <Line
                  data={loggedUsersChartData}
                  options={{ maintainAspectRatio: false }}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-2 mt-4">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-lg font-semibold mb-4">NPS</h2>
              <div className="w-full h-80">
                <Pie
                  data={FeedbackChartData}
                  options={{ maintainAspectRatio: false }}
                />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-lg font-semibold mb-4">Ratings</h2>
              <div className="w-full h-80">
                <Bar
                  data={FeedbackRepartitionChartData}
                  options={{ maintainAspectRatio: false }}
                />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-lg font-semibold mb-10">Stats</h2>
              <h3 className="text-lg font-semibold mb-4  flex flex-row">
                TOTAL{" "}
                <div className="flex flex-row ml-auto">
                  {requestFeedback
                    ? requestFeedback.total_feedbacks
                    : "Loading..."}
                  {" ratings"}
                </div>
              </h3>
              <h3 className="text-lg font-semibold mb-4  flex flex-row ">
                AVG{" "}
                <div className="flex flex-row ml-auto">
                  {requestFeedback
                    ? requestFeedback.average_rating
                    : "Loading..."}{" "}
                </div>
              </h3>
              <h3 className="text-lg font-semibold mb-4 flex flex-row">
                Monthly NPS
                <div
                  className="flex flex-row ml-auto"
                  style={{
                    color: requestFeedback
                      ? requestFeedback.nps >= 43
                        ? "green"
                        : "red"
                      : "black",
                  }}
                >
                  {requestFeedback ? requestFeedback.nps : "Loading..."}
                  {"%"}
                </div>
              </h3>
              <h3 className="text-md font-semibold mb-4 flex flex-row text-shadow_50">
                Avg SaaS NPS
                <div className="flex flex-row ml-auto">{"43 %"}</div>
              </h3>
            </div>
          </div>

          {/* New Charts */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 pt-2">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-lg font-semibold mb-4">Response Time</h2>
              <div className="w-full h-80">
                <Line
                  data={responseTimeData}
                  options={{ maintainAspectRatio: false }}
                />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-lg font-semibold mb-4">
                Transactions Per Minute (TPM)
              </h2>
              <div className="w-full h-80">
                <Bar
                  data={tpmChartData}
                  options={{ maintainAspectRatio: false }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "Financials" && (
        <div>
          <h2 className="text-lg font-bold mb-6">Financial Metrics</h2>
          <div className="flex flex-row gap-2 mb-2">
            <div className="bg-white p-6 rounded-lg shadow-md w-full">
              <h2 className="text-lg font-semibold text-gray-700">
                Total Volume
              </h2>
              <p className="text-3xl font-bold text-gray-900">
                {finances ? formatToCentimes(finances.total_volume) : []}
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md w-full">
              <h2 className="text-lg font-semibold text-gray-700">Total CA</h2>
              <p className="text-3xl font-bold text-gray-900">
                {finances ? formatToCentimes(finances.total_ca) : []}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-lg font-semibold mb-4">Payment Volume</h2>
              <div className="w-full h-80">
                <LineChart1
                  height={280}
                  className="p-0 m-0"
                  // all_values={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]}
                  current_values={finances ? finances.payments_volume : []}
                  // finalised_values={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]}
                  x_values={finances ? finances.dates : []}
                  format={"{value} €"}
                />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-lg font-semibold mb-4">Hexagone CA</h2>
              <div className="w-full h-80">
                <LineChart1
                  height={280}
                  className="p-0 m-0"
                  // all_values={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]}
                  current_values={finances ? finances.hexagone_ca : []}
                  // finalised_values={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]}
                  x_values={finances ? finances.dates : []}
                  format={"{value} €"}
                />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md h-60">
              <h2 className="text-lg font-semibold mb-4">Hexagone CA</h2>
              <PieChart1
                className="p-0 m-0 "
                serie_label="Répartition du CA"
                data={finances ? finances.repartition : []}
                fontSize={13}
              />
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md h-60">
              <h2 className="text-lg font-semibold mb-4">Hexagone CA</h2>
              <PieChart1
                className="p-0 m-0 "
                serie_label="Répartition du CA"
                data={finances ? finances.repartition : []}
                fontSize={13}
              />
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-lg font-semibold mb-4">Revenue vs Costs</h2>
              <div className="w-full h-80">
                <p className="text-center text-gray-500">
                  Graph coming soon...
                </p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-lg font-semibold mb-4">Profit Margins</h2>
              <div className="w-full h-80">
                <p className="text-center text-gray-500">
                  Graph coming soon...
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;

export const formatToCentimes = (value) => {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};
